import {
  AttitudeState,
  CFDFile,
  DomainState,
  PtapLocation,
  RHRepo,
  RunConfigCase,
  SimulationState,
  TyreState
} from '@aero-platform/shared'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'
import { FC, useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useRecoilValue } from 'recoil'
import { addSimChangeMetadata } from '../../api/submission-API'
import { useSimTemplateUpload } from '../../hooks/useSimTemplateUpload'
import { WorkflowSubmission } from '../../types/types'
import SimTemplateDropdown from './SimTemplateDropdown'
import SimChangeReasonModal from './SimChangeReasonModal'
import SimUploadStatus from './SimUploadStatus'
import { isF1TeamState, currentTeamState } from '../../utils/state-atoms'
import { CheckboxStyle, DropdownStyle } from '../../utils/styling-constants'
import Modal from '../Modal'
import CloseButton from '../buttons/CloseButton'
import { NASCAR28Teams } from '../../utils/constants'
import { selectRHRepoForAttitudeState } from '../../utils/attitudeStateDomain'

const prefix = '|'

const attitudeStateFields = [
  {
    name: 'frh',
    label: 'FRH',
    namePrefix: 'FR'
  },
  {
    name: 'rrh',
    label: 'RRH',
    namePrefix: 'RR'
  },
  {
    name: 'steer',
    label: 'Steer',
    namePrefix: 'S'
  },
  {
    name: 'roll',
    label: 'Roll',
    namePrefix: 'R'
  },
  {
    name: 'yaw',
    label: 'Yaw',
    namePrefix: 'Y'
  },
  {
    name: 'radius',
    label: 'Rad'
  },
  {
    name: 'speed',
    label: 'Spd'
  },
  {
    name: 'exhaust',
    label: 'Exh'
  },
  {
    name: 'spec',
    label: 'SPEC',
    nascar28Only: true
  },
  {
    name: 'radBlocker',
    label: 'RAD_BLOCKER',
    nascar28Only: true
  }
]
const title = {
  edit: 'Edit Case',
  new: 'Add New Case'
}

type NamePortion = {
  frh: string
  rrh: string
  yaw: string
  steer: string
  roll: string
  domain: string
  simulation: string
  fw: string
  drs: string
}

type CaseEditorModalProps = {
  mode?: string
  caseName?: string
  caseIndex?: number
  caseKey?: string
  submitting: boolean
  submission: WorkflowSubmission
  onConfirm: (runConfigCase: RunConfigCase, mode: string) => void
  onCancel: () => void
}

const formatFW = (fwNum: number | null) => {
  if (fwNum === null || fwNum === undefined) {
    return ''
  }
  let fw = fwNum.toFixed(1)
  fw = fw.replaceAll('.', 'p')

  return `${fw}FF`
}

const formatDRS = (drsNum: number | null) => {
  if (drsNum === null || drsNum === undefined) {
    return ''
  }
  let drs = drsNum.toFixed(0)
  return `${drs}RF`
}

const CaseEditorModal: FC<CaseEditorModalProps> = ({
  mode,
  caseIndex,
  caseName,
  caseKey,
  submitting,
  submission,
  onConfirm,
  onCancel
}) => {
  const [attitudeStates, setAttitudeStates] = useState<AttitudeState[]>(
    submission.runConfig.selectedAttitudeSet.attitudeStates || []
  )
  const [domainStates] = useState<DomainState[]>(
    submission.runConfig.selectedDomainSet?.domainStates || []
  )
  const [tyreSetStates] = useState<TyreState[]>(
    submission.runConfig.selectedTyreSet?.tyreStates || []
  )
  const [simulationStates] = useState<SimulationState[]>(
    submission.runConfig.selectedSimulationSet?.simulationStates || []
  )
  const [ptapLocationStates] = useState<PtapLocation[]>(
    submission.runConfig.selectedPtapLocationSet?.ptaplocationStates || []
  )
  const [rhRepoStates] = useState<RHRepo[]>(
    submission.runConfig.selectedRHRepoSet?.rhrepoStates || []
  )
  const [baseAttitudeState, setBaseAttitudeState] = useState<AttitudeState>()
  const [nameSegment, setNameSegment] = useState<NamePortion>({
    frh: '',
    rrh: '',
    yaw: '',
    steer: '',
    roll: '',
    fw: '',
    drs: '',
    domain: '',
    simulation: ''
  } as NamePortion)
  const [selectedAttitudeState, setSelectedAttitudeState] = useState<AttitudeState>(
    {} as AttitudeState
  )
  const [showAttitudeStateDialog, setShowAttitudeStateDialog] = useState(false)
  const [stateName, setStateName] = useState('')
  const [isInitializing, setIsInitializing] = useState(false)
  const [showSimChangeReasonModal, setShowSimChangeReasonModal] = useState(false)
  const [pendingFormData, setPendingFormData] = useState<any>(null)
  const originalSimTemplateRef = useRef<CFDFile | null>(null)
  const {
    control,
    setValue,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<RunConfigCase>({
    defaultValues: {
      selectedRHRepo: null,
      selectedAttitudeState: null,
      selectedDomainState: null,
      selectedTyreState: null,
      selectedSimulationState: null,
      selectedPtapLocation: null,
      name: '',
      key: '',
      selectedWingState: {
        fwState: null,
        drs: null
      },
      useDeformableTires: false,
      simTemplate: null
    }
  })

  const isF1Team = useRecoilValue(isF1TeamState)
  const currentTeam = useRecoilValue(currentTeamState)
  const isNascar28 = NASCAR28Teams.includes(currentTeam)
  const visibleAttitudeStateFields = attitudeStateFields.filter(f => !f.nascar28Only || isNascar28)
  const labelRequiredByF1Team = classNames('mb-2', { 'text-gray-400': !isF1Team })

  // Per-case .sim upload: direct-to-blob (files can be multi-GB) into a permanent
  // sim-templates folder; the case's simTemplate reference is set immediately while the
  // transfer continues in the background via the sim upload queue.
  const simFileInputRef = useRef<HTMLInputElement>(null)
  const sim = useSimTemplateUpload()

  // Per-case .sim upload: await the transfer AND confirm the blob is present in Azure before
  // attaching it to the case, so the case can never be saved against a sim that never landed.
  const handleSimTemplateUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (simFileInputRef.current) simFileInputRef.current.value = ''
    if (!file) return
    const landedTemplate = await sim.startUpload(file, { runName: submission.name || file.name })
    if (landedTemplate) {
      setValue('simTemplate', landedTemplate, { shouldValidate: true })
      toast.success('SIM file confirmed in Azure.')
    }
  }

  const getFormErrorMessage = name => {
    return errors[name] ? (
      <small className="error">{errors[name]?.message}</small>
    ) : (
      <small className="error">&nbsp;</small>
    )
  }

  const onSubmit = async formData => {
    // Don't let a case be saved while its freshly uploaded sim is still transferring or if it
    // failed to land in Azure — otherwise the run would reference a sim that isn't there.
    if (sim.isBusy) {
      toast.error('Wait for the SIM upload to be confirmed in Azure before saving.')
      return
    }
    if (sim.phase === 'failed') {
      toast.error('The SIM upload could not be confirmed in Azure. Please re-upload before saving.')
      return
    }
    // Every case needs a unique key so a single-case save can't collide with siblings.
    if (!formData.key) {
      formData.key = `${Date.now()}`
    }
    formData.selectedAttitudeState = { ...selectedAttitudeState }
    const attitude = formData.selectedAttitudeState as any
    if (attitude) {
      if (attitude.spec != null) attitude.spec = parseFloat(attitude.spec.toFixed(1))
      if (attitude.radBlocker != null)
        attitude.radBlocker = parseFloat(attitude.radBlocker.toFixed(1))
    }
    // Same attitude name is allowed as long as the sim file differs (multiple sim files per
    // attitude state). Only a case with the same name AND the same sim template is a duplicate.
    const formSimFile = formData.simTemplate?.file || ''
    const existingCase = submission.runConfig.selectedCases.find(caseItem => {
      if (caseItem.name !== formData.name) return false
      if (mode === 'edit' && caseItem.key === formData.key) return false
      return (caseItem.simTemplate?.file || '') === formSimFile
    })
    if (existingCase) {
      setError('name', {
        type: 'manual',
        message: 'A case with this name and sim template already exists.'
      })
      return
    }

    // Check if sim template changed in edit mode — require reason
    if (mode === 'edit' && originalSimTemplateRef.current) {
      const originalFile = originalSimTemplateRef.current.file || ''
      const newFile = formData.simTemplate?.file || ''
      if (originalFile && newFile && originalFile !== newFile) {
        // SIM file changed — show reason modal
        setPendingFormData(formData)
        setShowSimChangeReasonModal(true)
        return
      }
    }

    console.log('Submitting case with data:', formData)
    onConfirm && onConfirm(formData, mode as string)
  }

  // Called after user provides reason for SIM template change
  const handleSimChangeReasonConfirm = async (reason: string) => {
    setShowSimChangeReasonModal(false)
    if (!pendingFormData) return

    // Persist the SIM change metadata
    const caseName = pendingFormData.name || pendingFormData.key || ''
    const previousFile = originalSimTemplateRef.current?.file || ''
    const newFile = pendingFormData.simTemplate?.file || ''

    try {
      await addSimChangeMetadata(submission._id, {
        caseName,
        previousSimFile: previousFile,
        newSimFile: newFile,
        reason
      })
    } catch (err) {
      console.warn('Failed to persist SIM change metadata:', err)
    }

    console.log('Submitting case with data (after SIM change reason):', pendingFormData)
    onConfirm && onConfirm(pendingFormData, mode as string)
    setPendingFormData(null)
  }

  const handleConfirm = () => {
    onCancel && onCancel()
  }

  // Closing the editor aborts any in-flight sim upload so it doesn't keep running in the background.
  const handleClose = () => {
    sim.cancel()
    onCancel && onCancel()
  }

  useEffect(() => {
    if (mode === 'edit') {
      setIsInitializing(true)
      // Prefer the unique key so the exact case is loaded when multiple cases share a name
      // (same attitude, different sim file); fall back to name for legacy callers.
      const selectedCase =
        (caseKey &&
          submission.runConfig.selectedCases.find(caseItem => caseItem.key === caseKey)) ||
        submission.runConfig.selectedCases.find(caseItem => caseItem.name === caseName)
      if (selectedCase) {
        // Debug logging (can be removed in production)
        // console.log('=== DEBUGGING RH REPO ===')
        // console.log('Editing case:', selectedCase.name)
        // console.log('Case selectedRHRepo:', selectedCase.selectedRHRepo)
        setValue('key', selectedCase.key)
        setValue('name', selectedCase.name)
        isF1Team && setValue('selectedWingState.fwState', selectedCase.selectedWingState.fwState)
        isF1Team && setValue('selectedWingState.drs', selectedCase.selectedWingState.drs)
        isF1Team && setValue('useDeformableTires', selectedCase.useDeformableTires)
        if (isF1Team && selectedCase.selectedDomainState) {
          const tmpDomainState = domainStates.find(
            domainState => domainState.name === selectedCase.selectedDomainState.name
          )
          setValue('selectedDomainState', tmpDomainState as DomainState)
        }
        isF1Team && setValue('selectedTyreState', selectedCase.selectedTyreState)
        if (isF1Team && selectedCase.selectedTyreState) {
          const tmpTyreState = tyreSetStates.find(
            tyreState => tyreState.name === selectedCase.selectedTyreState.name
          )
          setValue('selectedTyreState', tmpTyreState as TyreState)
        }
        isF1Team && setValue('selectedSimulationState', selectedCase.selectedSimulationState)
        if (isF1Team && selectedCase.selectedSimulationState) {
          const tmpSimulationState = simulationStates.find(
            simulationState => simulationState.name === selectedCase.selectedSimulationState.name
          )
          setValue('selectedSimulationState', tmpSimulationState as SimulationState)
        }
        if (!isF1Team && selectedCase.selectedPtapLocation) {
          const tmpPtapLocation = ptapLocationStates.find(
            ptapLocation => ptapLocation.name === selectedCase.selectedPtapLocation.name
          )
          setValue('selectedPtapLocation', tmpPtapLocation as PtapLocation)
        }
        if (!isF1Team) {
          // First try to use the case's specific RH repo
          let rhRepoToUse = null

          if (selectedCase.selectedRHRepo) {
            // console.log('Looking for case RH repo with name:', selectedCase.selectedRHRepo.name)
            // console.log('Looking for suspensionTypeEncoding:', selectedCase.selectedRHRepo.suspensionTypeEncoding)
            rhRepoToUse = rhRepoStates.find(
              rhRepo =>
                rhRepo.name === selectedCase.selectedRHRepo.name &&
                rhRepo.suspensionTypeEncoding === selectedCase.selectedRHRepo.suspensionTypeEncoding
            )
            // Debug: log available encodings if exact match not found
            // if (!rhRepoToUse) {
            //   console.log('Exact match not found, available encodings:',
            //     rhRepoStates.filter(rh => rh.name === selectedCase.selectedRHRepo.name).map(rh => rh.suspensionTypeEncoding))
            // }
          }

          // If case doesn't have a specific RH repo or we can't find it, use the submission's current RH repo
          if (!rhRepoToUse && submission.runConfig.selectedRHRepoSet) {
            // console.log('Case RH repo not found, using submission RH repo set')
            // Use the first RH repo from the submission's selected set (or find a better way to determine which one)
            rhRepoToUse = rhRepoStates[0] // This might need to be more sophisticated
          }

          if (rhRepoToUse) {
            setValue('selectedRHRepo', rhRepoToUse)
            // console.log('✓ Set RH repo for edit mode:', rhRepoToUse.name, '/', rhRepoToUse.suspensionTypeEncoding)
          } else {
            console.warn('✗ Could not determine RH repo to use')
          }
        }

        // Set selected sim template for edit mode if present
        if (selectedCase.simTemplate) {
          console.log(
            `Setting sim template for edit mode: ${selectedCase.simTemplate.system} (${selectedCase.simTemplate.file})`
          )
          setValue('simTemplate', selectedCase.simTemplate)
          originalSimTemplateRef.current = selectedCase.simTemplate
        }

        const fw = isF1Team && formatFW(selectedCase.selectedWingState.fwState)
        const drs = isF1Team && formatDRS(selectedCase.selectedWingState.drs)
        let domain = ''
        if (isF1Team) {
          domain = prefix + selectedCase.selectedDomainState.name
        }
        let simulation = ''
        if (isF1Team) {
          simulation = prefix + selectedCase.selectedSimulationState.name
        }

        if (selectedCase.selectedAttitudeState) {
          const selectedCaseName = selectedCase.name
          if (selectedCaseName.includes(prefix)) {
            // custom case name
            const [name, ...deltas] = selectedCaseName.split(prefix) // get base name
            const oldAttitudeState = attitudeStates.find(
              attitudeState => attitudeState.name === name
            )
            setValue('selectedAttitudeState', oldAttitudeState as AttitudeState)
            setSelectedAttitudeState({
              name,
              frh: selectedCase.selectedAttitudeState.frh,
              rrh: selectedCase.selectedAttitudeState.rrh,
              yaw: selectedCase.selectedAttitudeState.yaw,
              steer: selectedCase.selectedAttitudeState.steer,
              roll: selectedCase.selectedAttitudeState.roll,
              radius: selectedCase.selectedAttitudeState.radius,
              speed: selectedCase.selectedAttitudeState.speed,
              exhaust: selectedCase.selectedAttitudeState.exhaust,
              spec: selectedCase.selectedAttitudeState.spec,
              radBlocker: selectedCase.selectedAttitudeState.radBlocker
            } as AttitudeState)
            let frh = '',
              rrh = '',
              yaw = '',
              steer = '',
              roll = ''
            deltas.forEach((delta, index) => {
              if (isF1Team && index >= deltas.length - 3) {
                // skip last two deltas
              } else if (delta.includes('FR') && !frh) {
                frh = prefix + delta
              } else if (delta.includes('RR') && !rrh) {
                rrh = prefix + delta
              } else if (delta.includes('Y') && !yaw) {
                yaw = prefix + delta
              } else if (delta.includes('S') && !steer) {
                steer = prefix + delta
              } else if (delta.includes('R') && !roll) {
                roll = prefix + delta
              }
            })

            setNameSegment({
              frh,
              rrh,
              yaw,
              steer,
              roll,
              fw,
              drs,
              domain,
              simulation
            })
            setBaseAttitudeState({ ...oldAttitudeState } as AttitudeState)
          } else {
            // default case name
            const tmpAttitudeState = attitudeStates.find(
              attitudeState => attitudeState.name === selectedCase.selectedAttitudeState.name
            )
            const mergedAttitudeState = {
              ...tmpAttitudeState,
              spec: selectedCase.selectedAttitudeState.spec,
              radBlocker: selectedCase.selectedAttitudeState.radBlocker
            } as AttitudeState
            setSelectedAttitudeState(mergedAttitudeState)
            setBaseAttitudeState({ ...tmpAttitudeState } as AttitudeState)
            setValue('selectedAttitudeState', tmpAttitudeState as AttitudeState)
          }
        }
      }
      // Reset initialization flag after a brief delay to allow all setValue calls to complete
      setTimeout(() => setIsInitializing(false), 100)
    }
  }, [])

  // Update name when baseAttitudeState or nameSegment changes
  useEffect(() => {
    if (baseAttitudeState) {
      let newName = `${nameSegment.frh}${nameSegment.rrh}${nameSegment.yaw}${nameSegment.steer}${nameSegment.roll}${nameSegment.domain}${nameSegment.simulation}`
      if (nameSegment.fw || nameSegment.drs) {
        newName = `${newName}${prefix}${nameSegment.fw}_${nameSegment.drs}`
      }
      setValue('name', baseAttitudeState.name + newName)
    } else if (selectedAttitudeState.name) {
      setValue('name', selectedAttitudeState.name)
    }
  }, [baseAttitudeState, nameSegment, selectedAttitudeState.name, setValue])

  const handleAttitudeStateChange = (
    attitudes: AttitudeState,
    shouldAutoPopulateRH: boolean = true
  ) => {
    // Encode attitude state
    // Pattern: multiply by 1000, pad to 4 digits with leading zeros, then strip trailing
    //          zeros under two conditions (keeping >= 3 chars):
    //   1. Value < 1.0 (leading zero in padded string) — always strip trailing zeros
    //   2. Value >= 1.0 — only strip when there are 2+ consecutive trailing zeros
    // This handles both IndyCar (0.650 -> "065") and NASCAR (1.380 -> "1380", 2.500 -> "250")
    const pad = (num: number) => {
      const absNum = Math.abs(num)

      if (absNum === 0) return '000'

      const scaled = Math.round(absNum * 1000)
      let str = String(scaled).padStart(4, '0')

      // Strip trailing zeros if:
      //   - string has a leading zero (value < 1.0), e.g. "0650" -> "065"
      //   - OR last two chars are both '0' (2+ trailing zeros), e.g. "2500" -> "250"
      // This preserves single trailing zeros for values >= 1.0, e.g. "1380" stays "1380"
      while (str.length > 3 && str.endsWith('0') && (str[0] === '0' || str.slice(-2) === '00')) {
        str = str.slice(0, -1)
      }

      return str
    }

    const attitudeState =
      `${attitudes.frh < 0 ? 'm' : 'p'}${pad(attitudes.frh)}_` +
      `${attitudes.rrh < 0 ? 'm' : 'p'}${pad(attitudes.rrh)}_` +
      `${attitudes.roll < 0 ? 'm' : 'p'}${pad(attitudes.roll)}_` +
      `${attitudes.yaw < 0 ? 'm' : 'p'}${pad(attitudes.yaw)}`

    // Only auto-populate ride height state if explicitly requested and not during initialization
    if (!isF1Team && shouldAutoPopulateRH && !isInitializing) {
      const matchedRHRepo = selectRHRepoForAttitudeState(rhRepoStates, attitudes, rhRepo =>
        rhRepo.suspensionTypeEncoding.includes(attitudeState)
      )
      if (matchedRHRepo) {
        // console.log('Auto-populating RH repo:', matchedRHRepo.name, '/', matchedRHRepo.suspensionTypeEncoding)
        setValue('selectedRHRepo', matchedRHRepo)
      } else {
        // console.log('No RH repo match found for attitude state:', attitudeState)
      }
    }
  }

  return (
    <Modal
      isOpen={true}
      withAction={false}
      easyClose={true}
      onCancel={() => handleClose()}
      title={title[mode || 'new']}
      showCancelButton={true}
      zIndex={30}
      onConfirm={handleConfirm}>
      <div className="relative">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full grid grid-cols-3 gap-x-2 gap-y-4 mt-8">
          <div className="field-box col-span-1">
            <Controller
              name="selectedAttitudeState"
              rules={{ required: 'Attitude state is required.' }}
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <div className="flex justify-between">
                    <label htmlFor={field.name} className="mb-2">
                      Attitude State <span className="text-red-500">*</span>
                    </label>
                    <Button
                      type="button"
                      label="+ Create New State"
                      pt={{
                        root: {
                          className: 'pt-0 hidden'
                        }
                      }}
                      link
                      onClick={() => {
                        setShowAttitudeStateDialog(true)
                      }}
                    />
                  </div>
                  <Dropdown
                    disabled={false}
                    value={field.value}
                    pt={DropdownStyle}
                    onChange={e => {
                      setSelectedAttitudeState(e.value)
                      setBaseAttitudeState({ ...e.value })
                      setNameSegment({
                        frh: '',
                        rrh: '',
                        yaw: '',
                        steer: '',
                        roll: '',
                        fw: '',
                        drs: '',
                        domain: '',
                        simulation: ''
                      })
                      handleAttitudeStateChange(e.value, true) // Allow auto-population when user changes attitude state
                      field.onChange(e.value)
                    }}
                    filter
                    filterBy="name"
                    className={classNames({ error: fieldState.error })}
                    options={attitudeStates}
                    optionValue="name"
                    optionLabel="name"
                    useOptionAsValue
                    placeholder="Select Starting State"
                    emptyMessage="&nbsp;&nbsp;No Data"
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>
          <div className="col-span-2 flex items-center justify-end space-x-2">
            {visibleAttitudeStateFields.map(field => (
              <div className="field-box" key={field.name}>
                <label htmlFor={field.name} className="mb-2">
                  {field.label}
                </label>
                {[
                  'frh',
                  'rrh',
                  'steer',
                  'roll',
                  'yaw',
                  ...(isNascar28 ? [] : ['spec', 'radBlocker'])
                ].includes(field.name) ? (
                  // Read-only display for attitude state fields
                  <InputText
                    id={field.name}
                    readOnly
                    value={
                      selectedAttitudeState[field.name as string]
                        ? ['spec', 'radBlocker'].includes(field.name)
                          ? selectedAttitudeState[field.name as string].toFixed(1)
                          : selectedAttitudeState[field.name as string].toFixed(3)
                        : '0.000'
                    }
                    className="w-full"
                    style={{ backgroundColor: '#f9fafb', color: '#6b7280' }}
                    title={`${field.label} is locked and cannot be edited`}
                  />
                ) : (
                  // Editable for other fields (radius, speed, exhaust) and spec/radBlocker for NASCAR-28
                  <InputNumber
                    id={field.name}
                    readOnly={submitting || !selectedAttitudeState}
                    mode="decimal"
                    maxFractionDigits={['spec', 'radBlocker'].includes(field.name) ? 1 : 1}
                    minFractionDigits={['spec', 'radBlocker'].includes(field.name) ? 1 : 0}
                    inputClassName="w-full"
                    value={selectedAttitudeState[field.name as string]}
                    onChange={e => {
                      setSelectedAttitudeState({
                        ...selectedAttitudeState,
                        [field.name]: e.value || 0
                      } as AttitudeState)
                      handleAttitudeStateChange(
                        {
                          ...selectedAttitudeState,
                          [field.name]: e.value || 0
                        } as AttitudeState,
                        true
                      ) // Allow auto-population when user manually changes attitude values

                      const newNameSegment = {
                        ...nameSegment,
                        [field.name]:
                          (e.value || 0) !== 0
                            ? prefix + (e.value || 0).toFixed(0) + field.namePrefix
                            : ''
                      }
                      setNameSegment(newNameSegment)
                    }}
                  />
                )}
                {getFormErrorMessage(field.name)}
              </div>
            ))}
          </div>

          {/* Domain State */}
          <div className="field-box col-span-1">
            <Controller
              name="selectedDomainState"
              rules={{ required: isF1Team ? 'Domain state is required.' : false }}
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className={labelRequiredByF1Team}>
                    Domain State {isF1Team && <span className="text-red-500">*</span>}
                  </label>
                  <Dropdown
                    disabled={!isF1Team}
                    value={field.value}
                    pt={DropdownStyle}
                    onChange={e => {
                      field.onChange(e.value)
                      setNameSegment({
                        ...nameSegment,
                        domain: prefix + e.value.name
                      })
                    }}
                    className={classNames({ error: fieldState.error })}
                    options={domainStates}
                    optionValue="name"
                    optionLabel="name"
                    useOptionAsValue
                    placeholder="Select Starting State"
                    emptyMessage="&nbsp;&nbsp;No Data"
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>

          {/* Tire State */}
          <div className="field-box col-span-1">
            <Controller
              name="selectedTyreState"
              rules={{ required: isF1Team ? 'Tire state is required.' : false }}
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className={labelRequiredByF1Team}>
                    Tire State {isF1Team && <span className="text-red-500">*</span>}
                  </label>
                  <Dropdown
                    disabled={!isF1Team}
                    value={field.value}
                    pt={DropdownStyle}
                    onChange={e => {
                      field.onChange(e.value)
                    }}
                    className={classNames({ error: fieldState.error })}
                    options={tyreSetStates}
                    optionValue="name"
                    optionLabel="name"
                    useOptionAsValue
                    placeholder="Select Starting State"
                    emptyMessage="&nbsp;&nbsp;No Data"
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>

          {/* Simulation State */}
          <div className="field-box col-span-1">
            <Controller
              name="selectedSimulationState"
              rules={{ required: isF1Team ? 'Simulation state is required.' : false }}
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className={labelRequiredByF1Team}>
                    Simulation State {isF1Team && <span className="text-red-500">*</span>}
                  </label>
                  <Dropdown
                    disabled={!isF1Team}
                    value={field.value}
                    pt={DropdownStyle}
                    onChange={e => {
                      field.onChange(e.value)
                      setNameSegment({
                        ...nameSegment,
                        simulation: prefix + e.value.name
                      })
                    }}
                    className={classNames({ error: fieldState.error })}
                    options={simulationStates}
                    optionValue="name"
                    optionLabel="name"
                    useOptionAsValue
                    placeholder="Select Starting State"
                    emptyMessage="&nbsp;&nbsp;No Data"
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>

          {/* Selected PTap Location */}
          {!isF1Team && (
            <div className="field-box col-span-1">
              <Controller
                name="selectedPtapLocation"
                rules={{ required: false }}
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <label htmlFor={field.name} className="mb-2">
                      Pressure Tap State
                    </label>
                    <Dropdown
                      value={field.value}
                      pt={DropdownStyle}
                      onChange={e => {
                        field.onChange(e.value)
                      }}
                      className={classNames({ error: fieldState.error })}
                      options={ptapLocationStates}
                      optionValue="name"
                      optionLabel="name"
                      useOptionAsValue
                      placeholder="Select PTAP Location State"
                      emptyMessage="&nbsp;&nbsp;No Data"
                    />
                    {getFormErrorMessage(field.name)}
                  </>
                )}
              />
            </div>
          )}

          {/* Selected RH Repo */}
          <div className="field-box col-span-1">
            <Controller
              name="selectedRHRepo"
              rules={{ required: !isF1Team ? 'RH Repo state is required.' : false }}
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className="mb-2">
                    Ride Height State {!isF1Team && <span className="text-red-500">*</span>}
                  </label>
                  <Dropdown
                    value={field.value || null}
                    disabled={submitting}
                    title={
                      field.value
                        ? `${field.value.name}/${field.value.suspensionTypeEncoding}`
                        : undefined
                    }
                    pt={DropdownStyle}
                    onChange={e => {
                      field.onChange(e.value)
                    }}
                    className={classNames({ error: fieldState.error })}
                    options={rhRepoStates}
                    optionValue="name"
                    valueTemplate={option => {
                      if (option) {
                        const fullText = `${option.name}/${option.suspensionTypeEncoding}`
                        // Smart truncation: if text is too long, show start + "..." + important end part
                        const maxLength = 25 // Adjust based on field width
                        let displayText = fullText
                        if (fullText.length > maxLength) {
                          const encoding = option.suspensionTypeEncoding
                          const truncatedName =
                            option.name.length > 12
                              ? option.name.substring(0, 9) + '...'
                              : option.name
                          displayText = `${truncatedName}/${encoding}`
                          // If still too long, truncate encoding too
                          if (displayText.length > maxLength) {
                            displayText = `${truncatedName}/...${encoding.slice(-8)}`
                          }
                        }

                        return (
                          <div title={fullText}>
                            <span className="truncate" style={{ maxWidth: '180px' }}>
                              {displayText}
                            </span>
                          </div>
                        )
                      } else {
                        return null
                      }
                    }}
                    itemTemplate={option => (
                      <span>
                        {option.name}/{option.suspensionTypeEncoding}
                      </span>
                    )}
                    useOptionAsValue
                    placeholder="Select RH file"
                    emptyMessage="&nbsp;&nbsp;No Data"
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>

          {/* Sim Template (show/edit) */}
          <div className="field-box col-span-1">
            <Controller
              name="simTemplate"
              rules={{ required: 'Sim template is required.' }}
              control={control}
              render={({ field, fieldState }) => {
                const val = field.value
                return (
                  <>
                    <label htmlFor={field.name} className="mb-2">
                      Sim Template <span className="text-red-500">*</span>
                    </label>
                    <SimTemplateDropdown
                      submissionId={submission._id}
                      submissionName={submission.name}
                      value={val}
                      onChange={v => {
                        console.log('Selected sim template:', v)
                        field.onChange(v)
                        // Picking an existing template supersedes any in-flight/failed upload so the
                        // user can recover without re-uploading and the selection isn't overwritten.
                        sim.reset()
                      }}
                      placeholder={'Select template or search caseId'}
                      className={classNames({ error: fieldState.error })}
                      disabled={submitting || sim.isBusy}
                    />
                    <div className="text-sm text-gray-500 mt-1">
                      {val
                        ? `(${val.rootType}-${val.fileType || val.rootType || val.system}) ${
                            val.file
                          }`
                        : 'None'}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        ref={simFileInputRef}
                        type="file"
                        accept=".sim"
                        onChange={handleSimTemplateUpload}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        label={sim.isBusy ? 'Uploading…' : 'Upload .sim'}
                        icon="pi pi-upload"
                        size="small"
                        outlined
                        disabled={submitting || sim.isBusy}
                        onClick={() => simFileInputRef.current?.click()}
                      />
                    </div>
                    <SimUploadStatus
                      phase={sim.phase}
                      progress={sim.progress}
                      error={sim.error}
                      fileName={sim.uploadedTemplate?.file}
                    />
                    {getFormErrorMessage(field.name)}
                  </>
                )
              }}
            />
          </div>

          {/* Generated Name */}
          <div className="field-box">
            <Controller
              name="name"
              control={control}
              disabled={submitting}
              rules={{ required: 'Name is required.' }}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className="mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <InputText
                    readOnly
                    id={field.name}
                    {...field}
                    className={classNames({ error: fieldState.error })}
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>

          <div className={classNames('field-box col-span-1', { '!hidden': !isF1Team })}>
            <Controller
              name="selectedWingState.fwState"
              control={control}
              disabled={submitting}
              rules={{ required: isF1Team ? 'FW State is required.' : false }}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className="mb-2">
                    FW State {isF1Team && <span className="text-red-500">*</span>}
                  </label>
                  <InputNumber
                    id={field.name}
                    mode="decimal"
                    maxFractionDigits={1}
                    value={field.value}
                    inputClassName={classNames('w-full', { error: fieldState.error })}
                    onChange={e => {
                      field.onChange(e.value)
                      const fw = formatFW(e.value)
                      setNameSegment({
                        ...nameSegment,
                        fw
                      })
                    }}
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>

          <div className={classNames('field-box col-span-1', { '!hidden': !isF1Team })}>
            <Controller
              name="selectedWingState.drs"
              control={control}
              disabled={submitting}
              rules={{ required: isF1Team ? 'DRS% is required.' : '' }}
              render={({ field, fieldState }) => (
                <>
                  <label htmlFor={field.name} className="mb-2">
                    DRS% {isF1Team && <span className="text-red-500">*</span>}
                  </label>
                  <InputNumber
                    id={field.name}
                    mode="decimal"
                    maxFractionDigits={1}
                    value={field.value}
                    inputClassName={classNames('w-full', { error: fieldState.error })}
                    onChange={e => {
                      field.onChange(e.value)
                      const drs = formatDRS(e.value)
                      setNameSegment({
                        ...nameSegment,
                        drs
                      })
                    }}
                  />
                  {getFormErrorMessage(field.name)}
                </>
              )}
            />
          </div>

          <div className={classNames('field-box col-span-3', { '!hidden': !isF1Team })}>
            <Controller
              name="useDeformableTires"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <div className="flex-grow flex items-center">
                    <Checkbox
                      inputId={field.name}
                      pt={CheckboxStyle}
                      checked={field.value}
                      onChange={e => {
                        field.onChange(e.checked)
                      }}></Checkbox>
                    <label htmlFor={field.name} className="ml-2 font-normal">
                      Tire Deformation
                    </label>
                  </div>
                </>
              )}
            />
          </div>

          <div className="w-full col-span-3 pt-8 flex items-center justify-end space-x-6">
            <Button
              type="submit"
              label="Save Case"
              loading={submitting}
              disabled={sim.isBusy || sim.phase === 'failed'}
            />
            <CloseButton label="Close" onClick={() => handleClose()} />
          </div>
        </form>
      </div>
      <Dialog
        header="Create New Attitude State"
        visible={showAttitudeStateDialog}
        style={{ width: '500px' }}
        onHide={() => {
          if (!showAttitudeStateDialog) {
            return
          }
          setShowAttitudeStateDialog(false)
        }}
        footer={
          <div className="flex justify-end space-x-2">
            <Button
              label="Save"
              onClick={() => {
                if (stateName) {
                  if (attitudeStates.find(state => state.name === stateName)) {
                    toast.error('Case name already exists.')
                    return
                  }
                  const newState = {
                    name: stateName,
                    frh: 0,
                    rrh: 0,
                    steer: 0,
                    roll: 0,
                    yaw: 0,
                    radius: 0,
                    speed: 0,
                    exhaust: 0
                  } as AttitudeState
                  setAttitudeStates([...attitudeStates, newState])
                  setValue('selectedAttitudeState', newState)
                  setSelectedAttitudeState(newState)
                  setBaseAttitudeState(undefined)
                  setShowAttitudeStateDialog(false)
                  return
                }
                toast.error('State name is required.')
              }}
            />
            <CloseButton label="Close" onClick={() => setShowAttitudeStateDialog(false)} />
          </div>
        }>
        <InputText
          placeholder="Enter state name"
          pt={{
            root: {
              className: 'w-full'
            }
          }}
          onChange={e => {
            setStateName(e.target.value)
          }}
        />
      </Dialog>
      {showSimChangeReasonModal && (
        <SimChangeReasonModal
          previousSimFile={originalSimTemplateRef.current?.file || ''}
          newSimFile={pendingFormData?.simTemplate?.file || ''}
          onConfirm={handleSimChangeReasonConfirm}
          onCancel={() => {
            setShowSimChangeReasonModal(false)
            setPendingFormData(null)
          }}
        />
      )}
    </Modal>
  )
}

export default CaseEditorModal
